

import { useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Grid2, Box, Select, MenuItem, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Constants } from '../constants';
import { NormalContainerWithHeader } from '../components/student/NormalComponent';
import AdminToolbar from '../../features/admin/components/AdminToolbar';
import DataGridSkeleton from '../components/DataGridSkeleton';
import MainDialogBox from '../components/Dialog/MainDialogBox';
import CustomPagination from './CustomPagination';
import { useDialog } from '../hook/useDialog';

const TableFilterTemplate = ({
    title,
    columns,
    rowsData = [],
    isLoading,
    filterSchema = Yup.object(),
    filterReset = () => { },
    filterInitialValues = {},
    renderFilterFields,
    onApplyFilters,
    filterTransformer = (values) => values,
    dataGridProps = {},
    filters = {},
    boxshodow = "",
    pageSize=10,
    isServerSide = false,
    serverPaginationModel,
    onServerPaginationChange,
    onServerSearchChange,
    totalServerRows = 0,
}) => {
    const { open, handleClickOpen, handleClose } = useDialog();
    const [localPaginationModel, setLocalPaginationModel] = useState({ page: 0, pageSize: pageSize});
    const [searchQuery, setSearchQuery] = useState('');

    const paginationModel = isServerSide ? serverPaginationModel : localPaginationModel;
    const setPaginationModel = isServerSide ? onServerPaginationChange : setLocalPaginationModel;

    const handleSearchChange = (value) => {
        if (isServerSide && onServerSearchChange) {
            onServerSearchChange(value);
        } else {
            setSearchQuery(value);
        }
    };

    const originalRows = useMemo(() => {
        return rowsData?.map((item, index) => ({
            id: item?.id || index + 1,
            sr_no: index + 1,
            ...item,
        })) || [];
    }, [rowsData]);

    const filteredRows = useMemo(() => {
        if (isServerSide) return originalRows;

        const lowerSearch = searchQuery.toLowerCase().trim();

        return originalRows.filter((item) => {
            const matchesSearch = !lowerSearch || Object.values(item).some(val =>
                val?.toString().toLowerCase().includes(lowerSearch)
            );

            const matchesFilters = filters
                ? Object.entries(filters).every(([key, value]) => {
                    if (!value) return true;
                    return item[key]?.toString().toLowerCase() === value.toLowerCase();
                })
                : true;

            return matchesSearch && matchesFilters;
        });
    }, [originalRows, searchQuery, filters, isServerSide]);


    const handleSubmitFilter = (values) => {
        const cleanFilters = Object.fromEntries(
            Object.entries(values).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        );
        const transformedFilters = filterTransformer(cleanFilters);
        onApplyFilters(transformedFilters);
        handleClose();
    };

    return (
        <>
            <NormalContainerWithHeader
                boxShadow={boxshodow}
                headerText={title}
                RightContent={
                    <AdminToolbar
                        onSearchChange={handleSearchChange}
                        filterIcon={Constants.customImages.FilterDark}
                        {...(renderFilterFields ? { onFilterClick: handleClickOpen } : undefined)}
                    />


                }
            >
                {isLoading ? (
                    <DataGridSkeleton rowsNum={pageSize} />
                ) : (
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        pagination
                        paginationMode={isServerSide ? "server" : "client"}
                        {...(isServerSide ? { rowCount: totalServerRows } : {})}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(newModel) => {
                            // Prevent DataGrid from overriding page to 0 in server mode if totalRows isn't strictly loaded
                            if (isServerSide && newModel.page === 0 && paginationModel.page > 0 && totalServerRows <= paginationModel.pageSize) {
                                return;
                            }
                            setPaginationModel(newModel);
                        }}
                        pageSizeOptions={Array.from(new Set([pageSize, 10, 15, 25, 50, 100])).sort((a,b)=>a-b)}
                        disableRowSelectionOnClick
                        sx={{
                            cursor: dataGridProps?.onRowClick ? 'pointer' : 'default', 
                        }}
                        slots={{
                            pagination: () => (
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'flex-end', p: 1, gap: 2 }}>
                                    {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2">Rows per page:</Typography>
                                        <Select
                                            value={paginationModel?.pageSize || pageSize}
                                            onChange={(e) => setPaginationModel(prev => ({ ...prev, pageSize: e.target.value, page: 0 }))}
                                            size="small"
                                            sx={{ height: 30 }}
                                        >
                                            {Array.from(new Set([pageSize, 10, 15, 25, 50, 100])).sort((a,b)=>a-b).map(size => (
                                                <MenuItem key={size} value={size}>{size}</MenuItem>
                                            ))}
                                        </Select>
                                    </Box> */}
                                    <CustomPagination
                                        paginationModel={paginationModel}
                                        setPaginationModel={setPaginationModel}
                                        totalRows={isServerSide ? totalServerRows : filteredRows.length}
                                        pageSize={paginationModel?.pageSize || pageSize}
                                    />
                                </Box>
                            ),
                        }}
                        {...dataGridProps}
                    />
                )}
            </NormalContainerWithHeader>

            <MainDialogBox open={open} handleClose={handleClose} width="40%">
                <Formik
                    initialValues={filterInitialValues}
                    enableReinitialize
                    validationSchema={filterSchema}
                    onSubmit={handleSubmitFilter}
                >
                    {({ values }) => (
                        <Form>
                            <Grid2 container spacing={2}>
                                {renderFilterFields({ values })}

                                <Grid2 size={6}>
                                    <Button variant="contained" onClick={() => { filterReset(); handleClose() }} fullWidth>
                                        Cancel Filter
                                    </Button>
                                </Grid2>
                                <Grid2 size={6}>
                                    <Button variant="contained" type="submit" fullWidth>
                                        Apply Filter
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </MainDialogBox>
        </>
    );
};

export default TableFilterTemplate;
